import { computed } from 'vue';
import { useStore } from 'vuex';

import { ENROLLMENT_STATUSES as STATUSES } from '@/shared/assets/constants';
import useRole from '@/shared/composables/useRole';
import useRolePermissions from '@/shared/composables/useRolePermissions';
import useTerms from '@/shared/composables/useTerms';

const STRICT_INVALID_STATUSES = {
  Placed: [STATUSES.ENROLLED, STATUSES.UNENROLLED, STATUSES.GRADUATED],
};

const PUBLISHES_NOTICE_STATUSES = [
  STATUSES.ACCEPTED,
  STATUSES.ENROLLED,
  STATUSES.GRADUATED,
  STATUSES.OFFERED,
  STATUSES.UNENROLLED,
  STATUSES.WITHDRAWN,
];

/**
 * @param {Object} options - Optional configuration object
 * @param {Object} options.subsidyProgram - The subsidy program object containing excluded statuses
 * @returns {
 *  allStatuses: () => array
 *  pendingStatuses: () => array
 *  releasedCurrentStatuses: () => array
 *  releasedNewStatuses: () => array
 *  removedStatuses: () => array
 *  getStatusText: (statusValue: string) => string
 *  getValidStatusChanges: (currentStatus: string, string: boolean) => array
 *  isPublishNoticeStatus: (status: string) => boolean
 * }
 */
export default function useEnrollmentStatuses(options = {}) {
  const store = useStore();
  const { isSpecialist, isManager } = useRole();
  const { enrollmentsEdit, enrollmentsAgent } = useRolePermissions();
  const { terms } = useTerms();

  const allStatuses = computed(() => {
    return pendingStatuses.value.concat(removedStatuses.value).concat(enrolledStatuses.value);
  });

  const enrolledStatuses = computed(() => {
    return filterExcludedEnrollmentStatuses([
      {
        text: terms.value.enrolled,
        value: STATUSES.ENROLLED,
        term: STATUSES.ENROLLED.toLowerCase(),
      },
      {
        text: terms.value.unenrolled,
        value: STATUSES.UNENROLLED,
        term: STATUSES.UNENROLLED.toLowerCase(),
      },
      {
        text: terms.value.graduated,
        value: STATUSES.GRADUATED,
        term: STATUSES.GRADUATED.toLowerCase(),
      },
    ]);
  });

  const pendingStatuses = computed(() => {
    return filterExcludedEnrollmentStatuses([
      {
        text: terms.value.selected,
        value: STATUSES.SELECTED,
        term: STATUSES.SELECTED.toLowerCase(),
      },
      {
        text: terms.value.proposed,
        value: STATUSES.PROPOSED,
        term: STATUSES.PROPOSED.toLowerCase(),
      },
      { text: terms.value.placed, value: STATUSES.PLACED, term: STATUSES.PLACED.toLowerCase() },
      { text: terms.value.offered, value: STATUSES.OFFERED, term: STATUSES.OFFERED.toLowerCase() },
      {
        text: terms.value.waitlisted,
        value: STATUSES.WAITLISTED,
        term: STATUSES.WAITLISTED.toLowerCase(),
      },
      {
        text: terms.value.accepted,
        value: STATUSES.ACCEPTED,
        term: STATUSES.ACCEPTED.toLowerCase(),
      },
    ]);
  });

  const releasedCurrentStatuses = computed(() => {
    return filterExcludedEnrollmentStatuses([
      { text: terms.value.offered, value: STATUSES.OFFERED, term: STATUSES.OFFERED.toLowerCase() },
      {
        text: terms.value.accepted,
        value: STATUSES.ACCEPTED,
        term: STATUSES.ACCEPTED.toLowerCase(),
      },
    ]);
  });

  const releasedNewStatuses = computed(() => {
    return filterExcludedEnrollmentStatuses([
      {
        text: terms.value.declined,
        value: STATUSES.DECLINED,
        term: STATUSES.DECLINED.toLowerCase(),
      },
      {
        text: terms.value.selected,
        value: STATUSES.SELECTED,
        term: STATUSES.SELECTED.toLowerCase(),
      },
      {
        text: terms.value.withdrawn,
        value: STATUSES.WITHDRAWN,
        term: STATUSES.WITHDRAWN.toLowerCase(),
      },
    ]);
  });

  const removedStatuses = computed(() => {
    return filterExcludedEnrollmentStatuses([
      {
        text: terms.value.declined,
        value: STATUSES.DECLINED,
        term: STATUSES.DECLINED.toLowerCase(),
      },
      {
        text: terms.value.withdrawn,
        value: STATUSES.WITHDRAWN,
        term: STATUSES.WITHDRAWN.toLowerCase(),
      },
      { text: terms.value.standby, value: STATUSES.STANDBY, term: STATUSES.STANDBY.toLowerCase() },
    ]);
  });

  const selectedStatusObject = computed(() => {
    return {
      text: store.state.config.terms.selected,
      value: STATUSES.SELECTED,
      term: STATUSES.SELECTED.toLowerCase(),
    };
  });

  const enrollmentAgentStatuses = computed(() => {
    return [
      { text: terms.value.offered, value: STATUSES.OFFERED, term: STATUSES.OFFERED.toLowerCase() },
      {
        text: terms.value.accepted,
        value: STATUSES.ACCEPTED,
        term: STATUSES.ACCEPTED.toLowerCase(),
      },
      {
        text: terms.value.withdrawn,
        value: STATUSES.WITHDRAWN,
        term: STATUSES.WITHDRAWN.toLowerCase(),
      },
    ];
  });

  function filterExcludedEnrollmentStatuses(statuses) {
    if (isManager) {
      const excludedStatuses = getEffectiveExcludedProviderStatuses();
      return statuses.filter((status) => !excludedStatuses.includes(status.value));
    }

    if (isSpecialist) {
      return statuses.filter(
        (status) =>
          !store.state.config.excluded_specialist_enrollment_statuses.includes(status.value),
      );
    }

    return statuses;
  }

  function getEffectiveExcludedProviderStatuses() {
    const { subsidyProgram } = options;

    if (subsidyProgram?.value?.excluded_provider_enrollment_statuses?.length > 0) {
      return subsidyProgram.value.excluded_provider_enrollment_statuses;
    }

    return store.state.config.excluded_provider_enrollment_statuses || [];
  }

  function getStatusText(statusValue) {
    return allStatuses.value.find((status) => status.value === statusValue)?.text || statusValue;
  }

  function getValidStatusChanges(currentStatus, strict) {
    if (enrollmentsEdit) {
      if (!strict) return allStatuses.value;

      return allStatuses.value.map((status) => ({
        ...status,
        disabled: STRICT_INVALID_STATUSES[currentStatus]?.includes(status.value),
      }));
    }
    if (enrollmentsAgent) return enrollmentAgentStatuses.value;
    return [];
  }

  function isPublishNoticeStatus(status) {
    return PUBLISHES_NOTICE_STATUSES.includes(status);
  }

  return {
    allStatuses,
    pendingStatuses,
    releasedCurrentStatuses,
    releasedNewStatuses,
    removedStatuses,
    selectedStatusObject,

    getStatusText,
    getValidStatusChanges,
    isPublishNoticeStatus,
  };
}
